import React, { useState } from 'react';

import { Input } from 'antd';

import ContactDropdown from '@components/ContactDropdown/ContactDropdown';
import { NwButton } from '@components/Gui/NwButton';
import { NwFakeFormItem } from '@components/Gui/NwForm/NwFakeFormWrapper';
import { NwFormItem } from '@components/Gui/NwForm/NwFormWrapper';
import NwSegmented from '@components/Gui/NwForm/NwSegmented';
import NwIcon from '@components/Gui/NwIcon';
import NwUpload from '@components/Gui/NwUpload';

import NwContentMediaSelector from '@containers/Art/NwContentMediaSelector';
import {
    GALLERY_MEDIA_SOURCE,
    GALLERY_MEDIA_SOURCE_OPTIONS,
} from '@containers/Art/Galleries/Galleries.constants';

import {
    ArtMediaFieldControl,
    ArtMediaUploadArea,
    MediaFieldsRow,
    MediaSourceRow,
} from './ArtMediaSourceFields.styles';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const FormItem = ({ variant, ...props }) => {
    const Item = variant === 'form' ? NwFormItem : NwFakeFormItem;
    return <Item {...props} />;
};

/**
 * Shared media source picker for Art editors: upload, uploaded reuse, Netwalk media, or Vimeo/YouTube embed.
 */
const ArtMediaSourceFields = ({
    embedApplyLabel = 'Set video',
    embedUrl = '',
    formItemVariant = 'fake',
    hideNetwalkActionWithoutModel = false,
    // per chi ha gia' una propria area di upload (es. la preview della cover news)
    hideUploadControl = false,
    netwalkMediaId,
    netwalkModelId,
    netwalkSelectLabel,
    onApplyEmbed,
    onEmbedUrlChange,
    onNetwalkModelChange,
    onSelectNetwalkMedia,
    onSelectUploadedMedia,
    onSourceChange,
    onUploadEnd,
    onUploadError,
    onUploadStart,
    showSourceSelector = true,
    source,
    sourceLabel = 'Media source',
    sourceOptions = GALLERY_MEDIA_SOURCE_OPTIONS,
    uploadLabel,
    uploadMaxCount,
    uploadMultiple = false,
    uploadText = 'click or drag to upload a picture or video',
    uploadedSelectorTitle = 'Select uploaded media',
    uploadedSelectorZIndex,
}) => {
    const [uploadedSelectorOpen, setUploadedSelectorOpen] = useState(false);

    const resolvedNetwalkSelectLabel =
        netwalkSelectLabel || (netwalkMediaId ? 'Change media' : 'Select media');

    const showNetwalkAction =
        source === GALLERY_MEDIA_SOURCE.NETWALK &&
        onSelectNetwalkMedia &&
        (!hideNetwalkActionWithoutModel || netwalkModelId);

    const showUploadedAction =
        source === GALLERY_MEDIA_SOURCE.UPLOADED && Boolean(onSelectUploadedMedia);

    const showEmbedAction = source === GALLERY_MEDIA_SOURCE.EMBED && Boolean(onApplyEmbed);

    const fieldsModifier = [
        showNetwalkAction ? 'is-netwalk' : '',
        showUploadedAction ? 'is-uploaded' : '',
        showEmbedAction ? 'is-embed' : '',
    ]
        .filter(Boolean)
        .join(' ');

    const openUploadedSelector = () => {
        setUploadedSelectorOpen(true);
    };

    const handleSourceChange = (nextSource) => {
        onSourceChange(nextSource);
        if (nextSource === GALLERY_MEDIA_SOURCE.UPLOADED && onSelectUploadedMedia) {
            setUploadedSelectorOpen(true);
        }
    };

    const handleSelectUploadedMedia = (selectedUpload) => {
        onSelectUploadedMedia(selectedUpload);
        setUploadedSelectorOpen(false);
    };

    const uploadControl = (
        <div className="media-action">
            <NwUpload
                localUpload
                maxCount={uploadMaxCount}
                maxSize={200}
                multiple={uploadMultiple}
                showList={false}
                types={['image', 'video']}
                onUploadEnd={onUploadEnd}
                onUploadError={onUploadError}
                onUploadStart={onUploadStart}
            >
                <ArtMediaUploadArea>
                    <NwIcon icon={byPrefixAndName.fasl['images']} />
                    {uploadText}
                </ArtMediaUploadArea>
            </NwUpload>
        </div>
    );

    return (
        <>
            {showSourceSelector && (
                <MediaSourceRow>
                    <FormItem
                        variant={formItemVariant}
                        label={sourceLabel}
                        control={
                            <NwSegmented
                                options={sourceOptions}
                                value={source}
                                onChange={handleSourceChange}
                            />
                        }
                    />
                </MediaSourceRow>
            )}
            <MediaFieldsRow className={fieldsModifier}>
                {source === GALLERY_MEDIA_SOURCE.NETWALK ? (
                    <FormItem
                        variant={formItemVariant}
                        label="Model"
                        control={
                            <ArtMediaFieldControl>
                                <ContactDropdown
                                    activeTypes={['mod']}
                                    customPlaceholder="Select a model"
                                    value={netwalkModelId}
                                    onChange={onNetwalkModelChange}
                                />
                            </ArtMediaFieldControl>
                        }
                    />
                ) : source === GALLERY_MEDIA_SOURCE.UPLOADED ? (
                    <FormItem
                        variant={formItemVariant}
                        label="Uploaded media"
                        control={
                            <ArtMediaFieldControl>
                                <NwButton
                                    htmlType="button"
                                    icon={byPrefixAndName.fasl['images']}
                                    label="Open uploaded media selector"
                                    primary
                                    onClick={openUploadedSelector}
                                />
                            </ArtMediaFieldControl>
                        }
                    />
                ) : source === GALLERY_MEDIA_SOURCE.EMBED ? (
                    <FormItem
                        variant={formItemVariant}
                        label="Vimeo or YouTube URL / embed"
                        control={
                            <ArtMediaFieldControl>
                                <Input
                                    autoComplete="off"
                                    placeholder="https://... or iframe embed"
                                    value={embedUrl}
                                    onChange={(event) => onEmbedUrlChange(event.target.value)}
                                />
                            </ArtMediaFieldControl>
                        }
                    />
                ) : hideUploadControl ? null : uploadLabel ? (
                    <FormItem
                        variant={formItemVariant}
                        label={uploadLabel}
                        control={<div className="media-upload-item">{uploadControl}</div>}
                    />
                ) : (
                    uploadControl
                )}

                {showNetwalkAction && (
                    <div className="media-source-action">
                        <NwButton
                            disabled={!hideNetwalkActionWithoutModel && !netwalkModelId}
                            htmlType="button"
                            icon={byPrefixAndName.fasl['images']}
                            label={resolvedNetwalkSelectLabel}
                            primary
                            onClick={onSelectNetwalkMedia}
                        />
                    </div>
                )}

                {showEmbedAction && (
                    <div className="media-source-action">
                        <NwButton
                            disabled={!embedUrl}
                            htmlType="button"
                            icon={byPrefixAndName.fasl['plus']}
                            label={embedApplyLabel}
                            primary
                            onClick={onApplyEmbed}
                        />
                    </div>
                )}
            </MediaFieldsRow>

            {uploadedSelectorOpen && (
                <NwContentMediaSelector
                    multiple={uploadMultiple}
                    title={uploadedSelectorTitle}
                    zIndex={uploadedSelectorZIndex}
                    onClose={() => setUploadedSelectorOpen(false)}
                    onSelectUpload={handleSelectUploadedMedia}
                />
            )}
        </>
    );
};

export default ArtMediaSourceFields;
